/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { HostedFieldsFieldDataFields } from 'braintree-web/hosted-fields';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import user from 'js/lib/user';

import { Button, InlineNotification, useToast } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';

import { setActiveWalletId, storeCreditCardToWallet } from 'bundles/account-settings/actions/EnterCardFormActions';
import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import EnterCardForm from 'bundles/account-settings/components/sections/payment-method/EnterCardForm';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import EnterCardFormStore from 'bundles/account-settings/stores/EnterCardFormStore';
import type { RecaptchaClient } from 'bundles/common/components/RecaptchaEnterprise';
import RecaptchaEnterprise from 'bundles/common/components/RecaptchaEnterprise';
import paymentsExperiments from 'bundles/epic/clients/__generated__/payments';
import {
  PAYMENTS_ENTERPRISE_RECAPTCHA_CHECKBOX_SITE_KEY,
  errorMapping,
} from 'bundles/payments-common/common/constants';
import type { BillingInfo } from 'bundles/payments-common/common/types';
import { getFormValidationErrors, isFormValid } from 'bundles/payments-common/utils/formValidation';

import _t from 'i18n!nls/account-settings';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  buttons: css`
    display: flex;
    gap: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-200);
  `,
};

const EnterCard = () => {
  const executeAction = useFluxibleAction();
  const { show: showToast } = useToast();
  const userId = user.get().id;

  const { billingInfo, braintreeHostedFieldsData, isWalletUpdate } = useFluxibleStore(EnterCardFormStore, (store) => ({
    billingInfo: store.getFormattedBillingInfo() as BillingInfo,
    braintreeHostedFieldsData: store.getBraintreeHostedFields()?.getState().fields as
      HostedFieldsFieldDataFields | undefined,
    isWalletUpdate: !!store.getPaymentWalletId(),
  }));

  const [updateStatus, setUpdateStatus] = useState<string | null>(null);
  const [statusUpdateErrorMessage, setStatusUpdateErrorMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [didRecaptchaSucceed, setDidRecaptchaSucceed] = useState(false);
  const [formValidationErrorCode, setFormValidationErrorCode] = useState<string | null>(null);
  const [disableSaveButtonForUnverifiedUser, setDisableSaveButtonForUnverifiedUser] = useState(false);

  const recaptchaClientRef = useRef<RecaptchaClient | undefined>(undefined);

  useEffect(() => {
    const isRequireEmailVerification = paymentsExperiments.get('requireEmailVerification');
    const shouldDisable = isRequireEmailVerification && !user.get().email_verified;
    setDisableSaveButtonForUnverifiedUser(shouldDisable);
    if (shouldDisable) {
      setFormValidationErrorCode(errorMapping.unverifiedEmail);
    }
  }, []);

  const saveCard = useCallback<() => void>(() => {
    const recaptchaToken = recaptchaClientRef.current?.getToken();
    if (!recaptchaToken) {
      return;
    }

    setIsSubmitted(true);
    if (!isFormValid('braintree', billingInfo, braintreeHostedFieldsData)) {
      setFormValidationErrorCode(errorMapping.invalidFields);
      return;
    }

    setUpdateStatus(UpdateStates.UPDATING);
    executeAction(storeCreditCardToWallet, {
      recaptchaToken,
      onSuccess: () => {
        executeAction(setActiveWalletId, null);
        SettingsActionCreators.refetchPaymentData(userId).then(() => {
          showToast({
            message: _t('Card updated successfully'),
            role: 'alert',
            durationMs: 0,
            prefixSlot: <SuccessIcon color="success" />,
          });
        });
      },
      onError: (errorMessage: string) => {
        recaptchaClientRef.current?.reset();
        setUpdateStatus(UpdateStates.FAILED);
        setStatusUpdateErrorMessage(errorMessage);
      },
    });
  }, [billingInfo, braintreeHostedFieldsData, executeAction, showToast, userId]);

  const onClickSaveButton = useCallback<(ev: React.MouseEvent<HTMLButtonElement>) => void>(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      ev.preventDefault();
      saveCard();
    },
    [saveCard]
  );

  const recaptchaOnSuccessHandler = useCallback<() => void>(() => {
    setDidRecaptchaSucceed(true);
  }, []);

  const recaptchaOnReadyHandler = useCallback<(client: RecaptchaClient) => void>((client: RecaptchaClient) => {
    recaptchaClientRef.current = client;
  }, []);

  const recaptchaOnErrorHandler = useCallback<() => void>(() => {
    setDidRecaptchaSucceed(false);
  }, []);

  const handleCancel = useCallback<() => void>(() => {
    executeAction(setActiveWalletId, null);
  }, [executeAction]);

  return (
    <div css={styles.container}>
      <EnterCardForm
        isSubmitted={isSubmitted}
        validationErrorCode={formValidationErrorCode}
        validationErrors={getFormValidationErrors('braintree', billingInfo, braintreeHostedFieldsData)}
      />
      <div>
        <RecaptchaEnterprise
          siteKey={PAYMENTS_ENTERPRISE_RECAPTCHA_CHECKBOX_SITE_KEY}
          onSuccess={recaptchaOnSuccessHandler}
          onReady={recaptchaOnReadyHandler}
          onError={recaptchaOnErrorHandler}
        />
      </div>

      <div>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {updateStatus === UpdateStates.FAILED && statusUpdateErrorMessage && (
            <InlineNotification severity="error">{statusUpdateErrorMessage}</InlineNotification>
          )}
        </div>
        <div css={styles.buttons}>
          <Button
            variant="primary"
            onClick={onClickSaveButton}
            loading={updateStatus === UpdateStates.UPDATING}
            disabled={!didRecaptchaSucceed || disableSaveButtonForUnverifiedUser}
          >
            {_t('Save')}
          </Button>
          {isWalletUpdate && (
            <Button variant="ghost" onClick={handleCancel}>
              {_t('Cancel')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnterCard;
